import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { useCan } from '@/hooks/useCan'
import { useSetCleaning, type ImpactRecoveryOption } from '../../impact-assessment'

/**
 * How each recovery scenario would bring an affected business service back (Andrew, 14 Oct 2026:
 * "When we show the impacted services it needs to show if there are recovery scenarios").
 *
 * What is affected is measured against the production scenario; these are the ways back. Back to
 * production is a real option once a lost location is cleaned ("If it can be cleaned fast, it can
 * be recovered so it is a valid recovery option"), so it says how long the cleaning is expected
 * to take where that has been entered.
 */
const AVAILABLE = new Set(['recoverable', 'recoverable_with_gaps', 'after_cleaning'])
export const hasRecoveryOption = (options: ImpactRecoveryOption[]) => options.some((o) => AVAILABLE.has(o.state))

export function optionText(t: TFunction, o: ImpactRecoveryOption) {
    const p = { name: o.name, locations: (o.reason?.locations ?? []).join(', '), time: o.reason?.cleanSeconds != null ? formatSecondsToDHM(o.reason.cleanSeconds) : '' }
    switch (o.state) {
        case 'recoverable': return t('recoverRequests.impact.options.available', '{{name}}: available', p)
        case 'recoverable_with_gaps': return t('recoverRequests.impact.options.withGaps', '{{name}}: available with gaps', p)
        case 'after_cleaning': return o.reason?.cleanSeconds != null
            ? t('recoverRequests.impact.options.afterCleaningTime', '{{name}}: once {{locations}} is cleaned (about {{time}})', p)
            : t('recoverRequests.impact.options.afterCleaning', '{{name}}: once {{locations}} is cleaned', p)
        case 'blocked': return t('recoverRequests.impact.options.notAvailable', '{{name}}: not available', p)
        case 'no_hierarchy': return t('recoverRequests.impact.options.noHierarchy', '{{name}}: no hierarchy for it', p)
        default: return t('recoverRequests.impact.options.cannotJudge', '{{name}}: cannot be judged', p)
    }
}

const TONE: Record<string, string> = {
    recoverable: 'border-green-600/50 bg-green-600/10 text-green-800 dark:text-green-300',
    recoverable_with_gaps: 'border-green-600/40 bg-green-600/5 text-green-800 dark:text-green-300',
    after_cleaning: 'border-sky-600/50 bg-sky-600/10 text-sky-800 dark:text-sky-300',
    blocked: 'border-red-600/40 bg-red-600/5 text-red-800 dark:text-red-300',
}

/** Better ways back first: available, then with gaps, then once cleaned; the rest last. */
const RANK: Record<string, number> = { recoverable: 0, recoverable_with_gaps: 1, after_cleaning: 2, blocked: 3, cannot_judge: 4, unknown: 4, no_hierarchy: 5 }
export function orderOptions(options: ImpactRecoveryOption[], recommendedId?: number | null) {
    return [...options].sort((a, b) => (RANK[a.state] ?? 4) - (RANK[b.state] ?? 4)
        || Number(b.scenarioId === recommendedId) - Number(a.scenarioId === recommendedId)
        || Number(b.isProduction) - Number(a.isProduction)
        || a.name.localeCompare(b.name))
}

const chip = (t: TFunction, o: ImpactRecoveryOption, extra = '') => (
    <span
        key={o.scenarioId}
        data-state={o.state}
        title={o.reason?.locations?.length ? `${optionText(t, o)} — ${o.reason.locations.join(', ')}` : optionText(t, o)}
        className={cn('max-w-full truncate rounded border px-1.5 py-px text-[11px]', TONE[o.state] ?? 'border-border bg-muted/40 text-muted-foreground', extra)}
    >
        {optionText(t, o)}
    </span>
)

/**
 * How a service can be brought back, kept short however many scenarios there are (Andrew,
 * 21 Oct 2026: "If the customer has many recovery scenarios the list will be too long").
 *
 * One line: the way back to use - the recommended scenario when it can recover the service, else
 * the best available - and how many of the scenarios can recover it ("2 of 5 scenarios"). The
 * full list opens from that count; beside the canvas, where the row itself is clickable, it is on
 * hover instead. A service no scenario can bring back says so in red.
 */
export function RecoveryOptions({ options, dense = false, recommendedId = null }: { options: ImpactRecoveryOption[]; dense?: boolean; recommendedId?: number | null }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const ordered = orderOptions(options, recommendedId)
    const available = ordered.filter((o) => AVAILABLE.has(o.state))
    const lead = available.find((o) => o.scenarioId === recommendedId) ?? available[0] ?? null
    const fullList = ordered.map((o) => optionText(t, o)).join('\n')
    const count = t('recoverRequests.impact.options.count', '{{count}} of {{total}} scenarios', { count: available.length, total: options.length })
    return (
        <span className={cn('flex flex-wrap items-center gap-1', dense ? 'mt-0.5' : 'mt-1')} data-testid="recovery-options">
            {lead ? chip(t, lead, lead.scenarioId === recommendedId ? 'font-semibold' : '') : (
                <span className="rounded border border-red-600/60 bg-red-600/15 px-1.5 py-px text-[11px] font-semibold text-red-800 dark:text-red-300" data-testid="no-recovery-option">
                    {t('recoverRequests.impact.options.none', 'No recovery scenario available')}
                </span>
            )}
            {lead && lead.scenarioId === recommendedId && <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground" data-testid="recovery-options-recommended">{t('recoverRequests.impact.options.recommended', 'Recommended')}</span>}
            {options.length > 1 && (dense ? (
                <span className="text-[11px] text-muted-foreground" title={fullList} data-testid="recovery-options-count">{count}</span>
            ) : (
                <button type="button" className="text-[11px] font-medium text-primary hover:underline" aria-expanded={open} onClick={() => setOpen((v) => !v)} title={fullList} data-testid="recovery-options-count">
                    {count} {open ? '▴' : '▾'}
                </button>
            ))}
            {open && !dense && <span className="flex basis-full flex-wrap gap-1" data-testid="recovery-options-all">{ordered.map((o) => chip(t, o))}</span>}
        </span>
    )
}

/**
 * Roughly how long each lost location takes to clean: what "once cleaned" waits for. Entered on
 * the assessment, saved when the field is left.
 */
export function CleaningTimes({ assessmentId, locations }: { assessmentId: number; locations: Array<{ id: number; name: string; cleanSeconds?: number | null }> }) {
    const { t } = useTranslation()
    const canChange = useCan('recover-requests.update')
    const save = useSetCleaning(assessmentId)
    const [problem, setProblem] = useState<string | null>(null)
    if (!locations.length) return null
    return (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-sky-600/40 bg-sky-600/5 px-3 py-2 text-xs" data-testid="impact-cleaning">
            <span className="flex items-center gap-1.5 font-semibold"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{t('recoverRequests.impact.cleaning.title', 'Time to clean each lost location')}</span>
            {locations.map((l) => (
                <CleaningField key={l.id} location={l} disabled={!canChange || save.isPending}
                    onSave={(hours) => { setProblem(null); save.mutate({ locationId: l.id, hours }, { onError: (e) => setProblem((e as { response?: { data?: { error?: string } } })?.response?.data?.error || (e as Error).message) }) }} />
            ))}
            {problem && <span role="alert" className="font-medium text-destructive">{problem}</span>}
        </div>
    )
}

function CleaningField({ location, disabled, onSave }: { location: { id: number; name: string; cleanSeconds?: number | null }; disabled: boolean; onSave: (hours: number | null) => void }) {
    const { t } = useTranslation()
    const saved = location.cleanSeconds == null ? '' : String(Math.round((location.cleanSeconds / 3600) * 10) / 10)
    const [value, setValue] = useState(saved)
    useEffect(() => setValue(saved), [saved])
    const commit = () => {
        const text = value.trim()
        if (text === saved) return
        onSave(text === '' ? null : Number(text))
    }
    return (
        <label className="flex items-center gap-1.5">
            <span>{location.name}</span>
            <input
                type="number" min={0} step={0.5} inputMode="decimal" value={value} disabled={disabled}
                onChange={(e) => setValue(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                aria-label={t('recoverRequests.impact.cleaning.hoursFor', 'Hours to clean {{name}}', { name: location.name })}
                placeholder="?"
                className="h-7 w-16 rounded-md border bg-background px-2 text-right tabular-nums"
            />
            <span className="text-muted-foreground">{t('recoverRequests.impact.cleaning.hours', 'hours')}</span>
        </label>
    )
}
